import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Info, LogOut, X } from "lucide-react";

// Extracted sub-components
import OverviewTab from "./members/OverviewTab";
import MyCodeTab from "./members/MyCodeTab";
import DailyCodeTab from "./members/DailyCodeTab";
import InvitationsTab from "./members/InvitationsTab";
import MembersListTab from "./members/MembersListTab";
import ActivityTab from "./members/ActivityTab";
import BookingsTab from "./members/BookingsTab";
import MessagesTab from "./members/MessagesTab";
import LiveSectionAdmin from "./members/LiveSectionAdmin";
import RoleInfoModal from "./members/RoleInfoModal";
import EditMemberModal from "./members/EditMemberModal";
import NotesModal from "./members/NotesModal";
import BookingReplyModal from "./members/BookingReplyModal";
import MessageReplyModal from "./members/MessageReplyModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleLabels = { admin: "Gruppe 1 (Administrator)", editor: "Gruppe 2 (Redaktør)", member: "Gruppe 3 (Medlem)" };
const roleColors = { admin: "bg-purple-600/20 text-purple-400", editor: "bg-blue-600/20 text-blue-400", member: "bg-green-600/20 text-green-400" };

const MembersPage = () => {
  const { member, isAdmin, getToken, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [showRoleInfo, setShowRoleInfo] = useState(false);

  // Admin data
  const [members, setMembers] = useState([]);
  const [history, setHistory] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);

  // Daily code
  const [dailyCode, setDailyCode] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyCountdown, setDailyCountdown] = useState("");

  // Modals
  const [editingMember, setEditingMember] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingReplyData, setBookingReplyData] = useState(null);
  const [sendingBookingReply, setSendingBookingReply] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);

  const getAuthHeaders = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), [getToken]);

  const fetchAdminData = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [membersRes, historyRes, bookingsRes, messagesRes, dailyRes] = await Promise.all([
        axios.get(`${API}/member-codes/list`, { headers }),
        axios.get(`${API}/member-codes/history`, { headers }),
        axios.get(`${API}/bookings`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/contact-messages`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/member-codes/daily-code`, { headers }).catch(() => ({ data: { active: false } })),
      ]);
      setMembers(membersRes.data);
      setHistory(historyRes.data);
      setBookings(bookingsRes.data);
      setContactMessages(messagesRes.data);
      if (dailyRes.data.active) setDailyCode(dailyRes.data);
      else setDailyCode(null);
    } catch (err) {
      console.error("Failed to fetch admin data:", err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, getAuthHeaders]);

  useEffect(() => { if (isAdmin) fetchAdminData(); }, [isAdmin, fetchAdminData]);

  // Daily code countdown
  useEffect(() => {
    if (!dailyCode?.expires_at) { setDailyCountdown(""); return; }
    const tick = () => {
      const diff = new Date(dailyCode.expires_at) - new Date();
      if (diff <= 0) { setDailyCountdown("Udløbet"); setDailyCode(null); return; }
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setDailyCountdown(`${h}t ${m}m`);
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [dailyCode]);

  // Daily code generation
  const generateDailyCode = async () => {
    setDailyLoading(true);
    try {
      const res = await axios.post(`${API}/member-codes/daily-code`, {}, { headers: getAuthHeaders() });
      setDailyCode({ active: true, code: res.data.code, expires_at: res.data.expires_at, used_by: [] });
      toast.success("Ny dagskode oprettet!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke oprette dagskode");
    } finally {
      setDailyLoading(false);
    }
  };

  // Booking handlers
  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`, {}, { headers: getAuthHeaders() });
      toast.success("Status opdateret"); fetchAdminData();
    } catch { toast.error("Fejl"); }
  };
  const deleteBooking = async (bookingId) => {
    if (!window.confirm("Slet denne booking?")) return;
    try { await axios.delete(`${API}/bookings/${bookingId}`, { headers: getAuthHeaders() }); toast.success("Slettet"); fetchAdminData(); }
    catch { toast.error("Fejl"); }
  };
  const saveNotes = async (bookingId, notes) => {
    try { await axios.put(`${API}/bookings/${bookingId}/notes`, { notes }, { headers: getAuthHeaders() }); toast.success("Gemt"); fetchAdminData(); setSelectedBooking(null); }
    catch { toast.error("Fejl"); }
  };
  const sendBookingReply = async (bookingId, replyText) => {
    if (!replyText.trim()) return;
    setSendingBookingReply(true);
    try { await axios.post(`${API}/bookings/${bookingId}/reply`, { reply: replyText }, { headers: getAuthHeaders() }); toast.success("Svar sendt!"); setBookingReplyData(null); fetchAdminData(); }
    catch { toast.error("Fejl"); } finally { setSendingBookingReply(false); }
  };

  // Message handlers
  const updateMessageStatus = async (messageId, status) => {
    try { await axios.put(`${API}/contact-messages/${messageId}/status`, { status }, { headers: getAuthHeaders() }); toast.success("Opdateret"); fetchAdminData(); }
    catch { toast.error("Fejl"); }
  };
  const sendReply = async (messageId, replyText) => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try { await axios.post(`${API}/contact-messages/${messageId}/reply`, { reply: replyText }, { headers: getAuthHeaders() }); toast.success("Sendt!"); setSelectedMessage(null); fetchAdminData(); }
    catch { toast.error("Fejl"); } finally { setSendingReply(false); }
  };
  const deleteMessage = async (messageId) => {
    if (!window.confirm("Slet denne besked?")) return;
    try { await axios.delete(`${API}/contact-messages/${messageId}`, { headers: getAuthHeaders() }); toast.success("Slettet"); fetchAdminData(); }
    catch { toast.error("Fejl"); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen pt-20 pb-12 px-4" data-testid="members-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <h1 className="text-4xl font-['Oswald'] font-bold chrome-text">MEDLEMSOMRÅDE</h1>
              {isAdmin && <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleColors[member?.role] || roleColors.member}`}>
                {roleLabels[member?.role] || "Medlem"}
              </span>}
            </div>
            <p className="text-gray-400">Velkommen, <span className="text-white font-medium">{member?.member_name}</span></p>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && <button onClick={() => setShowRoleInfo(true)} className="flex items-center gap-2 text-white font-['Oswald'] uppercase tracking-wider text-sm px-5 py-2.5 rounded-lg bg-purple-600/20 border border-purple-500/40 hover:bg-purple-600/30 hover:border-purple-400/60 transition-all" data-testid="role-info-btn">
              <Info className="w-4 h-4 text-purple-400" /> Gruppeforklaring
            </button>}
            <button onClick={handleLogout} className="flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors text-sm" data-testid="members-logout-btn">
              <LogOut className="w-4 h-4" /> Log ud
            </button>
          </div>
        </div>

        {/* Back button */}
        {activeTab !== "overview" && (
          <button onClick={() => setActiveTab("overview")} className="mb-6 text-blue-400 hover:text-blue-300 flex items-center gap-2" data-testid="back-to-overview">
            ← Tilbage til oversigt
          </button>
        )}

        {/* Tab content */}
        {activeTab === "overview" && <OverviewTab isAdmin={isAdmin} loading={loading} members={members} bookings={bookings} contactMessages={contactMessages} setActiveTab={setActiveTab} dailyCode={dailyCode} dailyCountdown={dailyCountdown} />}
        {activeTab === "my_code" && <MyCodeTab getAuthHeaders={getAuthHeaders} />}
        {activeTab === "daily_code" && isAdmin && <DailyCodeTab dailyCode={dailyCode} dailyCountdown={dailyCountdown} dailyLoading={dailyLoading} onGenerate={generateDailyCode} />}
        {activeTab === "invitations" && isAdmin && <InvitationsTab getAuthHeaders={getAuthHeaders} />}
        {activeTab === "members_list" && isAdmin && <MembersListTab members={members} loading={loading} isAdmin={isAdmin} onEditMember={setEditingMember} getAuthHeaders={getAuthHeaders} onRefresh={fetchAdminData} />}
        {activeTab === "activity" && isAdmin && <ActivityTab history={history} loading={loading} isAdmin={isAdmin} />}
        {activeTab === "bookings" && isAdmin && <BookingsTab bookings={bookings} onUpdateStatus={updateBookingStatus} onDelete={deleteBooking} onReply={setBookingReplyData} onNotes={setSelectedBooking} />}
        {activeTab === "messages" && isAdmin && <MessagesTab contactMessages={contactMessages} onUpdateStatus={updateMessageStatus} onDelete={deleteMessage} onReply={setSelectedMessage} />}
        {activeTab === "live_section" && isAdmin && <LiveSectionAdmin getAuthHeaders={getAuthHeaders} />}

        {/* Modals */}
        {showRoleInfo && <RoleInfoModal onClose={() => setShowRoleInfo(false)} />}
        {editingMember && <EditMemberModal member={editingMember} getAuthHeaders={getAuthHeaders} onClose={() => setEditingMember(null)} onRefresh={fetchAdminData} />}
        {selectedBooking && <NotesModal booking={selectedBooking} onSave={saveNotes} onClose={() => setSelectedBooking(null)} />}
        {bookingReplyData && <BookingReplyModal booking={bookingReplyData} onSend={sendBookingReply} onClose={() => setBookingReplyData(null)} sending={sendingBookingReply} />}
        {selectedMessage && <MessageReplyModal message={selectedMessage} onSend={sendReply} onClose={() => setSelectedMessage(null)} sending={sendingReply} />}
      </div>
    </div>
  );
};

export default MembersPage;
